import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { DEFAULT_ROLES, type AuthUser, type JWTPayload } from '@360dash/shared'

const TOKEN_KEY = '360dash_token'
const RETURN_URL_KEY = '360dash_returnTo'
// Refresh when less than 1 hour of token life remains
const REFRESH_THRESHOLD_MS = 60 * 60 * 1000

interface AuthContextValue {
  isAuthenticated: boolean
  isLoading: boolean
  user: AuthUser | null
  isAdmin: boolean
  login: (token: string) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

function writeStoredToken(token: string) {
  try {
    localStorage.setItem(TOKEN_KEY, token)
  } catch {
    // localStorage unavailable
  }
}

function clearStoredToken() {
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    // localStorage unavailable
  }
}

export function readStoredReturnUrl(): string | null {
  try {
    return localStorage.getItem(RETURN_URL_KEY)
  } catch {
    return null
  }
}

function writeStoredReturnUrl(path: string) {
  try {
    localStorage.setItem(RETURN_URL_KEY, path)
  } catch {
    // localStorage unavailable
  }
}

export function clearStoredReturnUrl() {
  try {
    localStorage.removeItem(RETURN_URL_KEY)
  } catch {
    // localStorage unavailable
  }
}

/**
 * Decodes a JWT payload without verifying the signature.
 * Signature verification happens server-side — this is client display/routing only.
 */
function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    return payload as JWTPayload
  } catch {
    return null
  }
}

function isTokenExpired(payload: JWTPayload): boolean {
  return Date.now() >= payload.exp * 1000
}

function payloadToUser(payload: JWTPayload): AuthUser {
  // Support both new tokens (sub) and legacy dashboard-auth tokens (client_id)
  const isAdmin = payload.type === 'admin'
  const id = payload.sub ?? payload.client_id ?? ''
  const role = isAdmin ? DEFAULT_ROLES.owner : DEFAULT_ROLES.client
  return {
    id,
    email: payload.email,
    type: payload.type,
    role: role.id,
    permissions: [...role.permissions],
  }
}

/**
 * AuthProvider — manages JWT-based authentication state.
 *
 * Token storage: localStorage key '360dash_token'
 * This matches the S360app pattern for cross-platform JWT compatibility.
 *
 * Silent refresh: when token has <1h remaining, a background call is made
 * to dashboard-auth. On failure, form data is saved to localStorage and
 * the user is redirected to login with a returnTo param.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
  }, [])

  const performLogout = useCallback((returnPath?: string) => {
    clearRefreshTimer()
    clearStoredToken()
    if (returnPath) {
      writeStoredReturnUrl(returnPath)
    }
    setUser(null)
    queryClient.clear()
    const loginPath = returnPath
      ? `/login?returnTo=${encodeURIComponent(returnPath)}`
      : '/login'
    navigate(loginPath, { replace: true })
  }, [clearRefreshTimer, navigate, queryClient])

  const scheduleRefresh = useCallback(
    (payload: JWTPayload) => {
      clearRefreshTimer()
      const msUntilRefreshNeeded = payload.exp * 1000 - Date.now() - REFRESH_THRESHOLD_MS
      if (msUntilRefreshNeeded <= 0) return
      refreshTimerRef.current = setTimeout(async () => {
        const authApiUrl = import.meta.env.VITE_AUTH_API_URL
        if (!authApiUrl) return
        try {
          const currentToken = readStoredToken()
          if (!currentToken) return
          const res = await fetch(`${authApiUrl}/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${currentToken}`,
            },
          })
          if (!res.ok) throw new Error(`Refresh failed: ${res.status}`)
          const json = await res.json() as { token: string }
          const newToken = json.token
          const newPayload = decodeJWT(newToken)
          if (!newPayload) throw new Error('Invalid refreshed token')
          writeStoredToken(newToken)
          setUser(payloadToUser(newPayload))
          scheduleRefresh(newPayload)
        } catch {
          // Refresh failed — save current path and redirect to login
          const returnPath = window.location.pathname + window.location.search
          performLogout(returnPath)
        }
      }, msUntilRefreshNeeded)
    },
    [clearRefreshTimer, performLogout]
  )

  // On mount: restore session from localStorage.
  // Uses refs to avoid dependency-array churn that re-fires the effect.
  const performLogoutRef = useRef(performLogout)
  performLogoutRef.current = performLogout
  const scheduleRefreshRef = useRef(scheduleRefresh)
  scheduleRefreshRef.current = scheduleRefresh

  useEffect(() => {
    try {
      const stored = readStoredToken()
      if (stored) {
        const payload = decodeJWT(stored)
        if (payload && !isTokenExpired(payload)) {
          setUser(payloadToUser(payload))
          scheduleRefreshRef.current(payload)
        } else {
          clearStoredToken()
        }
      }
    } catch {
      // localStorage unavailable
    }
    setIsLoading(false)

    const handleAuthExpired = () => {
      clearStoredToken()
      performLogoutRef.current()
    }
    window.addEventListener('auth:expired', handleAuthExpired)

    return () => {
      clearRefreshTimer()
      window.removeEventListener('auth:expired', handleAuthExpired)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = useCallback(
    (token: string) => {
      const payload = decodeJWT(token)
      if (!payload || isTokenExpired(payload)) {
        console.error('AuthProvider.login: invalid or expired token')
        return
      }
      writeStoredToken(token)
      setUser(payloadToUser(payload))
      scheduleRefresh(payload)
    },
    [scheduleRefresh]
  )

  const logout = useCallback(() => {
    performLogout()
  }, [performLogout])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        isLoading,
        user,
        isAdmin: user?.type === 'admin',
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
